import { useEffect, useRef, useState } from 'react'
import useUploadService from 'hooks/useUploadService'

interface ReferenceImagesForm {
  readImages: () => string[]
  writeImages: (images: string[]) => void
  onBusyChange: (busy: boolean) => void
}

/** Reconcile each upload with its own placeholder so deletion/reorder and parallel batches stay intact. */
export function usePromptReferenceImages(form: ReferenceImagesForm) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadingUrls, setUploadingUrls] = useState<Set<string>>(new Set())

  const { uploadFile } = useUploadService()
  const formRef = useRef(form)
  const mounted = useRef(true)
  const pendingBatches = useRef(0)
  const localUrls = useRef(new Set<string>())
  formRef.current = form

  const releaseUrl = (url: string) => {
    if (!localUrls.current.delete(url)) return

    URL.revokeObjectURL(url)
    if (mounted.current) setUploadingUrls(new Set(localUrls.current))
  }

  const uploadImages = async (files: File[]) => {
    if (!mounted.current || !files.length) return

    pendingBatches.current += 1
    setIsUploading(true)
    formRef.current.onBusyChange(true)
    const placeholders = files.map((file) => URL.createObjectURL(file))
    placeholders.forEach((url) => localUrls.current.add(url))
    formRef.current.writeImages([...formRef.current.readImages(), ...placeholders])
    setUploadingUrls(new Set(localUrls.current))

    try {
      for (let index = 0; index < files.length; index += 1) {
        if (!mounted.current) break

        const placeholder = placeholders[index]
        if (!localUrls.current.has(placeholder) || !formRef.current.readImages().includes(placeholder)) {
          releaseUrl(placeholder)
          continue
        }

        const file = files[index]
        let uploadedUrl: string | undefined
        try {
          uploadedUrl = await uploadFile(file, file.name.split('.').pop() || '')
        } catch (error) {
          console.error('Image upload failed:', error)
        }
        if (!mounted.current) break

        // A removed placeholder stays removed, and a moved one is replaced at its current position.
        formRef.current.writeImages(
          formRef.current
            .readImages()
            .flatMap((image) => (image === placeholder ? (uploadedUrl ? [uploadedUrl] : []) : [image]))
        )
        releaseUrl(placeholder)
      }
    } finally {
      pendingBatches.current -= 1
      if (mounted.current) {
        const busy = pendingBatches.current > 0
        setIsUploading(busy)
        formRef.current.onBusyChange(busy)
      }
    }
  }

  // RHF watchers re-render the caller when a preview is removed during an upload.
  useEffect(() => {
    const images = formRef.current.readImages()
    localUrls.current.forEach((url) => {
      if (!images.includes(url)) releaseUrl(url)
    })
  })

  useEffect(
    () => () => {
      mounted.current = false
      localUrls.current.forEach((url) => URL.revokeObjectURL(url))
      localUrls.current.clear()
      formRef.current.onBusyChange(false)
    },
    []
  )

  return { isUploading, uploadingUrls, uploadImages }
}
